import {
    NewHireConacts,
    OnboardingPackage,
    PayrollPartners,
} from '@features/digitalOnboarding/types';
import { Delete } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import { DeleteIcon } from '../../../../vectors/index';
import { useDeletePayrollPartner } from '@features/digitalOnboarding/hooks/useDeletePayrollPartner';
import { useAddOnboardingPackage } from '@features/digitalOnboarding/hooks/useAddOnboardingPackage';
import { useDeleteNewHireContact } from '@features/digitalOnboarding/hooks/useDeleteNewHireContact';

type AddOnboardingPackageModalProps = {
    isOpen: boolean;
    newHireContact?: boolean;
    handleClose: () => void;
    payrollData?: PayrollPartners | null;
    newHireContactData?: NewHireConacts | null;
    selectedCompanyId?: number | null;
    onboardingPackageData: OnboardingPackage | null;
};

const PayrollPartnerDeleteModal: FC<AddOnboardingPackageModalProps> = ({
    isOpen,
    handleClose,
    selectedCompanyId,
    onboardingPackageData,
    payrollData,
    newHireContact = false,
    newHireContactData = null,
}) => {
    const [errorText, setErrorText] = useState<string>('');

    const { deletePayrollPartner, isDeletingPayrollPartner } =
        useDeletePayrollPartner({
            setErrorText,
        });
    const { deletenewHireContact, isDeletingnewHireContact } =
        useDeleteNewHireContact({
            setErrorText,
        });

    const { addOnboardPackage, isAddingOnboardPackage } = useAddOnboardingPackage(
        {
            setErrorText,
        },
    );

    const onSubmit = () => {
        const options = {
            onSuccess: (data: { message: string }) => {
                toast.success(data.message);
                handleClose();
            },
        };

        onboardingPackageData
            ? addOnboardPackage(
                {
                    package_id: onboardingPackageData?.id,
                    template_ids: '',
                    joblisting_ids: '',
                    ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
                },
                options,
            )
            : payrollData && payrollData?.id
                ? deletePayrollPartner(
                    {
                        partner_id: payrollData?.id,
                        ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
                    },
                    options,
                )
                : newHireContact &&
                newHireContactData?.id &&
                deletenewHireContact(
                    {
                        id: newHireContactData?.id,
                    },
                    options,
                );
    };

    return (
      <Dialog
        open={isOpen}
        onClose={() => {
          handleClose();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <br />
          <DeleteIcon />
          <br />
          <Typography variant="h4" sx={{ marginTop: 3 }}>
            Are you sure to delete{' '}
            <b>
              {payrollData
                ? payrollData?.onboarding_partner_name
                : newHireContactData
                  ? newHireContactData?.name
                  : ''}
            </b>
            ?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginBottom: 3 }}>
            {errorText && <Alert severity="error">{errorText}</Alert>}

            <LoadingButton
              startIcon={<Delete />}
              onClick={onSubmit}
              variant="contained"
              color="error"
              size="small"
              fullWidth
              loading={
                isDeletingPayrollPartner ||
                isAddingOnboardPackage ||
                isDeletingnewHireContact
              }
              disabled={
                isDeletingPayrollPartner ||
                isAddingOnboardPackage ||
                isDeletingnewHireContact
              }
            >
              Delete
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
    );
};

export default PayrollPartnerDeleteModal;
